import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Link as LinkIcon,
  Unlink as UnlinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayCircleFilledWhite as PlayCircleFilledWhiteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAxios from "../../../../hooks/useAxios";
import AttachQuizModal from "../../Modals/AttachQuizModal/AttachQuizModal";
import ConfirmationModal from "../../../common/ConfirmationModal/ConfirmationModal";
import "./QuizManagementTable.css";

const QuizManagementTable = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [hintToggleLoading, setHintToggleLoading] = useState({});
  const [hintToggleError, setHintToggleError] = useState(null);
  const [browserLockToggleLoading, setBrowserLockToggleLoading] = useState({});
  const [browserLockToggleError, setBrowserLockToggleError] = useState(null);
  const [isTerminateSessionsModalOpen, setIsTerminateSessionsModalOpen] = useState(false);
  const [quizToTerminate, setQuizToTerminate] = useState(null);
  const [terminateSessionsLoading, setTerminateSessionsLoading] = useState(false);
  const axiosInstance = useAxios();
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/quizzes/all");
      if (response.data.success) {
        setQuizzes(response.data.quizzes);
      } else {
        setError(response.data.message || "Failed to fetch quizzes.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };



  const handleAttachQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setIsAttachModalOpen(true);
  };

  const handleAttachModalClose = (refresh = false) => {
    setIsAttachModalOpen(false);
    setSelectedQuiz(null);
    if (refresh) {
      fetchQuizzes();
    }
  };

  const handleEditQuiz = (quiz) => {
    navigate(`/quiz-review/${quiz.id}`);
  };

  const handleDeleteQuiz = (quiz) => {
    setQuizToDelete(quiz);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setQuizToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!quizToDelete) return;
    
    try {
      await axiosInstance.delete(`/quizzes/${quizToDelete.id}`);
      // Refresh the quizzes list after deletion
      fetchQuizzes();
      handleCloseDeleteModal();
    } catch (err) {
      console.error('Failed to delete quiz:', err);
      alert('Failed to delete quiz. Please try again.');
    }
  };

  const handlePreviewQuiz = async (quiz) => {
    try {
      // Create a new session for preview (same as QuizCard)
      const response = await axiosInstance.post(`/sessions/create`, { quiz_id: quiz.id });
      
      if (response.data.success) {
        // Navigate to the session page with the session ID (same as QuizCard)
        navigate(`/sessions/${response.data.session.id}`);
      } else {
        alert(response.data.message || 'Failed to start quiz preview. Please try again.');
      }
    } catch (err) {
      console.error('Error starting quiz preview:', err);
      alert(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleHintToggle = async (quiz) => {
    if (!quiz) return;
    
    setHintToggleLoading(prev => ({ ...prev, [quiz.id]: true }));
    setHintToggleError(null);
    setBrowserLockToggleError(null); // Clear any browser lock errors
    
    try {
      const response = await axiosInstance.patch(`/quizzes/${quiz.id}/settings`, {
        hints_enabled: !quiz.hints_enabled
      });
      
      if (response.data.success) {
        // Update the quiz in the local state
        setQuizzes(prev => prev.map(q => 
          q.id === quiz.id 
            ? { ...q, hints_enabled: !q.hints_enabled }
            : q
        ));
        setHintToggleError(null);
      }
    } catch (err) {
      console.error('Failed to update hint settings:', err);
      if (err.response?.data?.message) {
        setHintToggleError(err.response.data.message);
        setQuizToTerminate(quiz);
        setIsTerminateSessionsModalOpen(true);
      } else {
        setHintToggleError('Failed to update hint settings. Please try again.');
      }
    } finally {
      setHintToggleLoading(prev => ({ ...prev, [quiz.id]: false }));
    }
  };

  const handleBrowserLockToggle = async (quiz) => {
    if (!quiz) return;
    
    setBrowserLockToggleLoading(prev => ({ ...prev, [quiz.id]: true }));
    setBrowserLockToggleError(null);
    setHintToggleError(null); // Clear any hint errors
    
    try {
      const response = await axiosInstance.patch(`/quizzes/${quiz.id}/settings`, {
        browser_lock: !quiz.browser_lock
      });
      
      if (response.data.success) {
        // Update the quiz in the local state
        setQuizzes(prev => prev.map(q => 
          q.id === quiz.id 
            ? { ...q, browser_lock: !q.browser_lock }
            : q
        ));
        setBrowserLockToggleError(null);
      }
    } catch (err) {
      console.error('Failed to update browser lock settings:', err);
      if (err.response?.data?.message) {
        setBrowserLockToggleError(err.response.data.message);
        setQuizToTerminate(quiz);
        setIsTerminateSessionsModalOpen(true);
      } else {
        setBrowserLockToggleError('Failed to update browser lock settings. Please try again.');
      }
    } finally {
      setBrowserLockToggleLoading(prev => ({ ...prev, [quiz.id]: false }));
    }
  };

  const handleTerminateAllSessions = async () => {
    if (!quizToTerminate) return;
    
    setTerminateSessionsLoading(true);
    try {
      // First, terminate all active sessions
      const response = await axiosInstance.patch(`/quizzes/${quizToTerminate.id}/terminate-sessions`);
      
      if (response.data.success) {
        // Now try to toggle the setting that was originally requested
        if (hintToggleError) {
          await handleHintToggle(quizToTerminate);
          setHintToggleError(null);
        } else if (browserLockToggleError) {
          await handleBrowserLockToggle(quizToTerminate);
          setBrowserLockToggleError(null);
        }
        setIsTerminateSessionsModalOpen(false);
        setQuizToTerminate(null);
      }
    } catch (err) {
      console.error('Failed to terminate sessions:', err);
      if (hintToggleError) {
        setHintToggleError('Failed to terminate sessions. Please try again.');
      } else if (browserLockToggleError) {
        setBrowserLockToggleError('Failed to terminate sessions. Please try again.');
      }
    } finally {
      setTerminateSessionsLoading(false);
    }
  };

  const handleCloseTerminateModal = () => {
    setIsTerminateSessionsModalOpen(false);
    setQuizToTerminate(null);
    setHintToggleError(null);
    setBrowserLockToggleError(null);
  };

  const getAttachedClassesInfo = (quiz) => {
    if (!quiz.attached_classes || quiz.attached_classes.length === 0) {
      return "Not attached to any class";
    }

    const activeClasses = quiz.attached_classes.filter(cls => cls.is_active);
    const inactiveClasses = quiz.attached_classes.filter(cls => !cls.is_active);

    return (
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
        {activeClasses.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
            //   icon={<span style={{ fontWeight: 700, color: '#fff' }}>A</span>}
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
                    {activeClasses.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    Active&nbsp;class{activeClasses.length !== 1 ? 'es' : ''}
                  </Typography>
                </Box>
              }
              color="success"
              size="small"
              sx={{
                px: 1.5,
                py: 0.5,
                // background: 'linear-gradient(90deg, #43a047 60%, #66bb6a 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.95rem',
                minWidth: 0,
                borderRadius: 'full',
              }}
            />
          </Box>
        )}
        {inactiveClasses.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
            //   icon={<span style={{ fontWeight: 700, color: '#757575' }}>I</span>}
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#757575' }}>
                    {inactiveClasses.length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#757575' }}>
                    Inactive&nbsp;class{inactiveClasses.length !== 1 ? 'es' : ''}
                  </Typography>
                </Box>
              }
              color="default"
              size="small"
              sx={{
                px: 1.5,
                py: 0.5,
                background: '#e0e0e0',
                color: '#757575',
                fontWeight: 700,
                fontSize: '0.95rem',
                minWidth: 0,
                borderRadius: 'full',
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const getQuizStatus = (quiz) => {
    if (quiz.is_trial) {
      return <Chip label="Trial" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <TableContainer component={Paper} className="quiz-management-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="quiz-table-header">Title</TableCell>
              <TableCell className="quiz-table-header">ID</TableCell>
              <TableCell className="quiz-table-header">Hint Mode</TableCell>
              <TableCell className="quiz-table-header">Browser Lock</TableCell>
              <TableCell className="quiz-table-header">Class Attachments</TableCell>
              <TableCell className="quiz-table-header">Created</TableCell>
              <TableCell className="quiz-table-header">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quizzes.length > 0 ? (
              quizzes.map((quiz) => (
                <TableRow key={quiz.id} className="table-row">
                  <TableCell className="quiz-title-cell">
                    <Typography variant="body2" fontWeight={500}>
                      {quiz.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" fontFamily="monospace">
                      {quiz.unique_identifier}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip 
                      title={quiz.hints_enabled 
                        ? "Students get three attempts for each question with hints on wrong answers"
                        : "Students get one attempt per question with no hints"
                      }
                      arrow
                    >
                      <Chip
                        label={`Hint Mode ${quiz.hints_enabled ? 'Enabled' : 'Disabled'}`}
                        color={quiz.hints_enabled ? 'success' : 'default'}
                        variant="filled"
                        onClick={() => handleHintToggle(quiz)}
                        disabled={hintToggleLoading[quiz.id]}
                        style={{
                          cursor: 'pointer',
                          color: quiz.hints_enabled ? '#fff' : '#333',
                          backgroundColor: quiz.hints_enabled ? '#2e7d32' : '#e0e0e0',
                          border: 'none',
                          fontWeight: 600
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip 
                      title={quiz.browser_lock 
                        ? "Students cannot leave the session page until completion"
                        : "Students can freely navigate away from the session page"
                      }
                      arrow
                    >
                      <Chip
                        label={`Browser Lock ${quiz.browser_lock ? 'Enabled' : 'Disabled'}`}
                        color={quiz.browser_lock ? 'warning' : 'default'}
                        variant="filled"
                        onClick={() => handleBrowserLockToggle(quiz)}
                        disabled={browserLockToggleLoading[quiz.id]}
                        style={{
                          cursor: 'pointer',
                          color: quiz.browser_lock ? '#fff' : '#333',
                          backgroundColor: quiz.browser_lock ? '#ed6c02' : '#e0e0e0',
                          border: 'none',
                          fontWeight: 600
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell className="attachments-cell">
                    {getAttachedClassesInfo(quiz)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(quiz.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Start Quiz Preview (Creates Session)" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewQuiz(quiz)}
                          color="primary"
                        >
                          <PlayCircleFilledWhiteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Quiz" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleEditQuiz(quiz)}
                          color="secondary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Quiz" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteQuiz(quiz)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Attach to Classes" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleAttachQuiz(quiz)}
                          color="info"
                        >
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No quizzes found. Create your first quiz to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AttachQuizModal
        open={isAttachModalOpen}
        onClose={handleAttachModalClose}
        quiz={selectedQuiz}
      />

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete Quiz"
        subtitle={`Are you sure you want to delete the quiz "${quizToDelete?.title}"?`}
        isDanger={true}
        onSubmit={handleConfirmDelete}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This action cannot be undone. All quiz data, questions, and student attempts will be permanently removed from all attached classes.
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        open={isTerminateSessionsModalOpen}
        onClose={handleCloseTerminateModal}
        title="Active Sessions Detected"
        subtitle={`There are active sessions for this quiz. To change ${hintToggleError ? 'hint mode' : 'browser lock'}, you need to terminate all active sessions first.`}
        isDanger={false}
        onSubmit={handleTerminateAllSessions}
        submitText={terminateSessionsLoading ? "Terminating..." : "Terminate All Sessions"}
        cancelText="Cancel"
      >
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This will end all ongoing quiz sessions for this quiz. Students will lose their progress and will need to start over.
        </Typography>
      </ConfirmationModal>
    </>
  );
};

export default QuizManagementTable; 